import { FC, useEffect, useState } from 'react';
import Image from 'next/image';
import slide1 from '@public/assets/pages/interwill/video-guide/slide1.png';
import slide2 from '@public/assets/pages/interwill/video-guide/slide2.png';
import step1 from '@public/assets/pages/interwill/video-guide/step1.png';
import step2 from '@public/assets/pages/interwill/video-guide/step2.png';
import step3 from '@public/assets/pages/interwill/video-guide/step3.png';
import cn from 'classnames';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { useIwBreakPoints } from '@/_pages/interwill/hooks/useIwBreakPoints';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { VideoGuideProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../containers/ModalContainerV2';

export const VideoGuideModal: FC<VideoGuideProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'videoGuide' });

  const modals = useModalsStore();

  const { isMobile, isMobileXs } = useIwBreakPoints();

  const [step, setStep] = useState(1);

  const maxSteps = isMobile ? 5 : 3;

  const isDone = (step === 3 && !isMobile) || step === 5;

  useEffect(() => {
    if (!isMobile && step >= 3) {
      setStep(3);
    }
  }, [isMobile]);

  return (
    <ModalContainerStyled pinBottom title={''}>
      {step === 1 && (
        <div>
          <ImageStyled src={slide1} alt="guide slide" width={800} height={330} />
          <SlideContent>
            <Title>{t('slide1.title')}</Title>
            <span>{t('slide1.text')}</span>
          </SlideContent>
        </div>
      )}
      {step === 2 && (
        <div>
          <ImageStyled src={slide2} alt="guide slide" width={800} height={330} />
          <SlideContent>
            <Title>{t('slide2.title')}</Title>
            <List>
              <li>{t('slide2.text1')}</li>
              <li>{t('slide2.text2')}</li>
              <li>{t('slide2.text3')}</li>
            </List>
          </SlideContent>
        </div>
      )}
      {step === 3 && !isMobile && (
        <div>
          <SlideContentSteps>
            <Row>
              <ImageRounded src={step1} alt="guide slide" width={311} height={133} />
              <Col>
                <TitleNoMargin>{t('slide3.title')}</TitleNoMargin>
                <b>{t('slide3.step', { number: 1 })}</b>
                <span>{t('slide3.text1')}</span>
              </Col>
            </Row>
            <Row>
              <ImageRounded src={step2} alt="guide slide" width={311} height={133} />
              <Col>
                <b>{t('slide3.step', { number: 2 })}</b>
                <span>{t('slide3.text2')}</span>
              </Col>
            </Row>
            <Row>
              <ImageRounded src={step3} alt="guide slide" width={311} height={133} />
              <Col>
                <b>{t('slide3.step', { number: 3 })}</b>
                <span>{t('slide3.text3')}</span>
              </Col>
            </Row>
          </SlideContentSteps>
        </div>
      )}

      {step === 3 && isMobile && (
        <div>
          <ImageStyled src={step1} alt="guide slide" width={311} height={133} />
          <SlideContent>
            <Col>
              <TitleNoMargin>{t('slide3.title')}</TitleNoMargin>
              <div>
                <b>{t('slide3.step', { number: 1 })}</b>
              </div>
              <span>{t('slide3.text1')}</span>
            </Col>
          </SlideContent>
        </div>
      )}

      {step === 4 && isMobile && (
        <div>
          <ImageStyled src={step2} alt="guide slide" width={311} height={133} />
          <SlideContent>
            <Col>
              <TitleNoMargin>{t('slide3.title')}</TitleNoMargin>
              <div>
                <b>{t('slide3.step', { number: 2 })}</b>
              </div>
              <span>{t('slide3.text2')}</span>
            </Col>
          </SlideContent>
        </div>
      )}

      {step === 5 && isMobile && (
        <div>
          <ImageStyled src={step3} alt="guide slide" width={311} height={133} />
          <SlideContent>
            <Col>
              <TitleNoMargin>{t('slide3.title')}</TitleNoMargin>
              <div>
                <b>{t('slide3.step', { number: 3 })}</b>
              </div>
              <span>{t('slide3.text3')}</span>
            </Col>
          </SlideContent>
        </div>
      )}

      <Buttons>
        <ButtonIwBack
          icon={isMobileXs && <Icon name="caret-left" size="s20" />}
          onClick={() => {
            if (step <= 1) return;
            setStep(step - 1);
          }}
          className={cn({ hidden: step <= 1 })}
          type="default"
        >
          {!isMobileXs && t('btnBack')}
        </ButtonIwBack>
        <Step>
          {step}/{maxSteps}
        </Step>
        <ButtonIw
          icon={!isDone && isMobileXs && <Icon name="caret-right" size="s20" />}
          onClick={() => {
            if (step >= maxSteps) {
              modals.closeModal();
              return;
            }
            setStep(step + 1);
          }}
          type={isDone || !isMobileXs ? 'primary' : 'default'}
        >
          {(isDone || !isMobileXs) && t(isDone ? 'btnDone' : 'btnNext')}
        </ButtonIw>
      </Buttons>
    </ModalContainerStyled>
  );
};

const Buttons = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${media.iwMobile} {
    padding: 16px;
  }
`;

const ButtonIwBack = styled(ButtonIw)`
  &.hidden {
    visibility: hidden;
  }
`;

const SlideContent = styled.div`
  padding: 24px;
  font-size: 14px;
  min-height: 174px;

  ${media.iwMobile} {
    padding: 16px;
    min-height: 222px;
  }

  @media (max-width: 390px) {
    min-height: 154px;
  }
`;

const Title = styled.b`
  font-size: 22px;
  margin-bottom: 8px;
  display: block;

  @media (max-width: 390px) {
    font-size: 16px;
  }
`;

const ModalContainerStyled = styled(ModalContainerV2)`
  padding: 0;
  width: 800px !important;

  .ant-modal-header {
    display: none;
  }

  .ant-modal-content {
    width: 800px !important;
    max-width: 800px !important;
    padding: 0 !important;
  }

  .ant-modal-body {
    border-radius: 12px;
    overflow: hidden;
  }

  ${media.iwMobile} {
    width: 100% !important;

    .ant-modal-content {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
`;

const Step = styled.b`
  font-size: 16px;
  color: ${COLORS.systemDark200};
`;

const List = styled.ul`
  margin-left: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
`;

const ImageStyled = styled(Image)`
  ${media.iwMobile} {
    width: 100%;
    height: auto;
  }
`;

const ImageRounded = styled(ImageStyled)`
  border-radius: 10px;
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleNoMargin = styled(Title)`
  margin: 0;
`;

const SlideContentSteps = styled(SlideContent)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
